// Single Axios instance for talking to the Diool core API.
//   * baseURL = VITE_DIOOL_CORE_URL
//   * Authorization: Bearer <token> from Auth0 (token getter wired by AuthProvider)
//   * X-BEVersion header on every request
// Loading state is owned by TanStack Query (see <Loader/>), not by this interceptor.
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { env } from './env';

type TokenGetter = () => Promise<string | null>;
type LogoutHandler = () => void;

let tokenGetter: TokenGetter = async () => null;
let logoutHandler: LogoutHandler = () => {};
let logoutInFlight = false;

// 401 grace window. Rationale: a single 401 must NOT immediately log the user
// out — that turns any backend/Auth0 misconfig into an infinite redirect loop
// (the SPA is wrapped in withAuthenticationRequired, so logging out instantly
// sends the user back to Auth0, which sends them back to the SPA, ...).
//
// Policy:
//   * Any 2xx response clears the 401 counter.
//   * First 401 → log a warning and reject; do NOT logout.
//   * Subsequent 401 within UNAUTH_WINDOW_MS of the previous 401, with no
//     intervening successful response → treat as a real expired-session and
//     trigger logout.
const UNAUTH_WINDOW_MS = 30_000;
let lastUnauthAt = 0;
let unauthStreak = 0;

export function setAuthTokenGetter(getter: TokenGetter): void {
  tokenGetter = getter;
}

export function setAuthLogoutHandler(handler: LogoutHandler): void {
  logoutHandler = handler;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.app.dioolCoreUrl,
});

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  config.headers.set('X-BEVersion', env.app.xBeVersion);
  try {
    const token = await tokenGetter();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
  } catch {
    // Swallow token errors; they will surface on the actual API response.
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    // Any successful response means the session is alive; reset the 401 streak.
    unauthStreak = 0;
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const now = Date.now();
    const withinWindow = now - lastUnauthAt < UNAUTH_WINDOW_MS;
    unauthStreak = withinWindow ? unauthStreak + 1 : 1;
    lastUnauthAt = now;

    if (unauthStreak < 2) {
      // First 401 in this window — likely a transient misconfig or a single
      // unauthorized endpoint. Don't log the user out on a single failure.
      // eslint-disable-next-line no-console
      console.warn(
        '[axios] 401 from API; not logging out yet (will logout if another 401 follows within',
        `${UNAUTH_WINDOW_MS}ms).`,
        error.config?.url,
      );
      return Promise.reject(error);
    }

    if (!logoutInFlight) {
      logoutInFlight = true;
      // eslint-disable-next-line no-console
      console.warn('[axios] sustained 401s; triggering Auth0 logout.');
      try {
        logoutHandler();
      } catch {
        // Ignore — Auth0 redirect may already be in flight.
      }
    }
    return Promise.reject(error);
  },
);
